import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import * as yup from 'yup';
import ForgotPasswordModal from '../../components/modals/ForgotPasswordModal';
import SuccessModal from '../../components/modals/SuccessModal';
import EmailVerificationModal from '../../components/modals/EmailVerificationModal';
import CaptchaModal from '../../components/modals/CaptchaModal';
import CaptchaStatusModal from '../../components/modals/CaptchaStatusModal';
import { useAuth } from '../../hooks/useAuth';
import { useGoogleOAuth } from '../../hooks/useGoogleOAuth';
import { useMicrosoftOAuth } from '../../hooks/useMicrosoftOAuth';
import { useFacebookOAuth } from '../../hooks/useFacebookOAuth';
import { useAppleOAuth } from '../../hooks/useAppleOAuth';
// import PasswordValidationPopup from '../../components/modals/PasswordValidationPopup';
import '../../styles/components/login.css';
// import AppleNameModal from '../../components/modals/AppleNameModal';
import OAuthLoadingOverlay from '../../components/OAuthLoadingOverlay';

// Validation schema using yup
const loginSchema = yup.object().shape({
  username: yup
    .string()
    .required('Email is required')
    .test(
      'allowed-chars',
      'Email can only contain letters, numbers, and the characters . _ - @',
      value => !value || /^[a-zA-Z0-9@._-]+$/.test(value)
    )
    .test('valid-format', 'Please enter a valid email address', value =>
      !value || /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
    )
    .test('tld-length', 'Please enter a valid email address', value => {
      if (!value) return true;
      const tld = value.match(/\.([a-zA-Z]+)$/);
      return !!tld && tld[1].length <= 6;
    })
    .test('domain-length', 'Please enter a valid email address', value => {
      if (!value) return true;
      const domain = value.split('@')[1] || '';
      const domainName = domain.split('.').slice(0, -1).join('.');
      return domainName.length <= 30;
    })
    .test('no-gibberish', 'Please enter a valid email address', value => {
      if (!value) return true;
      const [localPart, domain] = value.split('@');
      const domainName = (domain || '').split('.').slice(0, -1).join('.');
      const hasGibberish = (str: string) => /[^aeiou0-9@._-]{5,}/i.test(str);
      return !hasGibberish(localPart) && !hasGibberish(domainName);
    }),

  password: yup
    .string()
    .required('Password is required')
    .test('min-length', 'Password must be at least 6 characters', value => !value || value.length >= 6)
});

// Map backend login errors to a professional modal title/description
const getLoginErrorDisplay = (error?: string): { title: string; description: string } => {
  const normalized = (error || '').toLowerCase();
  // Invalid credentials with an attempts-remaining warning from the backend.
  // Check this BEFORE the lockout branch — the warning text contains the phrase
  // "before your account is locked", which would otherwise match the lockout regex.
  const attemptsMatch = (error || '').match(/(\d+)\s+attempts?\s+remaining/i);
  if (attemptsMatch) {
    const attempts = Number(attemptsMatch[1]);
    const lockoutMatch = (error || '').match(/locked for\s+(\d+)\s+(minute|hour)s?/i);
    const lockoutUnit = lockoutMatch ? (Number(lockoutMatch[1]) === 1 ? lockoutMatch[2] : `${lockoutMatch[2]}s`) : 'minutes';
    const lockoutPhrase = lockoutMatch ? `${lockoutMatch[1]} ${lockoutUnit}` : '15 minutes';
    return {
      title: 'Login Details Don’t Match',
      description: `Those credentials don’t match our records. You have ${attempts} ${attempts === 1 ? 'attempt' : 'attempts'} remaining before your account is locked for ${lockoutPhrase}.`
    };
  }
  // Lockout (HTTP 423) — surface the specific wait time the backend included.
  if (/temporarily locked|too many failed login attempts/.test(normalized)
      || /account.*(locked|disabled|suspended)|\b(locked|disabled|suspended)\b/.test(normalized)) {
    const retryMatch = (error || '').match(/try again in\s+(\d+)\s+(minute|hour)s?/i);
    const unit = retryMatch ? (Number(retryMatch[1]) === 1 ? retryMatch[2] : `${retryMatch[2]}s`) : '';
    const waitPhrase = retryMatch ? `in ${retryMatch[1]} ${unit}` : 'a few minutes';
    return {
      title: 'Account Temporarily Locked',
      description: `For your security, we’ve paused sign-in on this account due to too many failed login attempts. Please try again ${waitPhrase}, or contact support if you need help.`
    };
  }
  if (/invalid\s+(username|user\s*name|email|credentials|password)|incorrect\s+(username|password|credentials)|wrong\s+(username|password|credentials)/.test(normalized)) {
    return {
      title: 'Login Details Don’t Match',
      description: 'Those credentials don’t match our records. Please double-check your email and password, or tap “Forgot password?” to reset it.'
    };
  }
  if (/account.*(deactivated|inactive)|\bdeactivated\b/.test(normalized)) {
    return {
      title: 'Your Account Is Deactivated',
      description: 'This account is no longer active, so we can’t sign you in right now. If you’d like to reactivate it or believe this is a mistake, please reach out to our support team and we’ll help you get back in.'
    };
  }
  if (/not\s+verified|verify\s+your\s+email|email\s+not\s+verified/.test(normalized)) {
    return {
      title: 'Please Verify Your Email',
      description: 'We just need to confirm your email first. Check your inbox for the verification link, then come back and sign in.'
    };
  }
  if (/user\s+not\s+found|does\s+not\s+exist|no\s+(such\s+)?account/.test(normalized)) {
    return {
      title: 'We Couldn’t Find That Account',
      description: 'We don’t see an account with those details. Please double-check what you entered, or create a new account to get started.'
    };
  }
  return {
    title: 'We Couldn’t Sign You In',
    description: error || 'Something went wrong on our end. Please try again in a moment.'
  };
};

// Polished copy for OAuth login success (Google/Gmail, Microsoft, Facebook, Apple)
const getOAuthLoginSuccessDisplay = (provider: string, message?: string): { title: string; description: string } => {
  const trimmed = (message || '').trim();
  const prefix = trimmed ? `${trimmed.replace(/[.!]$/, '')}. ` : '';
  return {
    title: `Signed in with ${provider}`,
    description: `${prefix}Redirecting you to your devices...`
  };
};

// Polished copy for OAuth login failures — handles cancel, popup, permission, network, and not-configured states
const getOAuthLoginErrorDisplay = (provider: string, error?: string): { title: string; description: string } => {
  const normalized = (error || '').toLowerCase();
  if (/cancel|aborted|closed/.test(normalized)) {
    return {
      title: 'Sign-in Cancelled',
      description: `You closed the ${provider} sign-in before finishing. Please try again when you're ready.`
    };
  }
  if (/popup.*block|blocked.*popup|popup_blocked/.test(normalized)) {
    return {
      title: 'Popup Blocked',
      description: `Your browser blocked the ${provider} sign-in window. Please allow popups for this site and try again.`
    };
  }
  if (/not\s+configured|oauth.*not|not\s+available|configuration/.test(normalized)) {
    return {
      title: `${provider} Sign-in Unavailable`,
      description: `${provider} sign-in isn't set up on this environment yet. Please try another option or contact support.`
    };
  }
  if (/denied|access\s+denied|permission|consent/.test(normalized)) {
    return {
      title: 'Permission Required',
      description: `We couldn't finish signing you in because access wasn't granted. Please approve the requested permissions and try again.`
    };
  }
  if (/network|connection|offline|timeout|unreachable/.test(normalized)) {
    return {
      title: 'Connection Issue',
      description: `We couldn't reach ${provider} right now. Please check your internet connection and try again.`
    };
  }
  if (/account.*exists|already\s+registered|different\s+provider/.test(normalized)) {
    return {
      title: 'Try a Different Sign-in Method',
      description: `This email is already registered with a different sign-in method. Please sign in the way you registered, or reset your password.`
    };
  }
  return {
    title: `Unable to Sign In with ${provider}`,
    description: error || `We couldn't complete sign-in with ${provider}. Please try again in a moment.`
  };
};

// Map backend forgot-password errors to a professional modal title/description.
// Used for the "Couldn't Send Reset Link" failure modal — keeps copy consistent
// across the in-login forgot password modal and the standalone Forgot Password page.
export const getForgotPasswordErrorDisplay = (error?: string): { title: string; description: string } => {
  const normalized = (error || '').toLowerCase();
  if (/too\s+many|rate.?limit|throttle|429/.test(normalized)) {
    const retryMatch = (error || '').match(/(?:try again in|wait)\s+(\d+)\s+(second|minute|hour)s?/i);
    const unit = retryMatch ? (Number(retryMatch[1]) === 1 ? retryMatch[2] : `${retryMatch[2]}s`) : '';
    const waitPhrase = retryMatch ? `in ${retryMatch[1]} ${unit}` : 'in a few minutes';
    return {
      title: 'Too Many Requests',
      description: `For your security, we’ve paused password reset requests for this email. Please try again ${waitPhrase}.`
    };
  }
  if (/network|connection|offline|timeout|unreachable/.test(normalized)) {
    return {
      title: 'Connection Issue',
      description: 'We couldn’t reach our servers just now. Please check your internet connection and try again.'
    };
  }
  if (/invalid\s+email|not\s+a\s+valid\s+email/.test(normalized)) {
    return {
      title: 'Invalid Email Address',
      description: 'That email address doesn’t look right. Please check the spelling and try again.'
    };
  }
  return {
    title: 'Couldn’t Send Reset Link',
    description: 'We weren’t able to send a reset link right now. Please try again in a moment, or contact support if the issue persists.'
  };
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, forgotPassword, isAuthenticated, clearError } = useAuth();
  const { googleLoginWithUserType, cancelOAuth: cancelGoogleOAuth } = useGoogleOAuth();
  const {
    microsoftLoginWithUserType,
    cancelOAuth: cancelMicrosoftOAuth,
    processRedirectResult: processMicrosoftRedirectResult,
  } = useMicrosoftOAuth();
  const {
    facebookLoginWithUserType,
    cancelOAuth: cancelFacebookOAuth,
    processRedirectResult: processFacebookRedirectResult,
  } = useFacebookOAuth();
  const {
    appleLoginWithUserType,
    cancelOAuth: cancelAppleOAuth,
    processRedirectResult: processAppleRedirectResult,
  } = useAppleOAuth();

  // Tracks which OAuth provider has an in-flight login. null when idle.
  const [oauthInProgress, setOauthInProgress] = useState<null | 'Google' | 'Microsoft' | 'Facebook' | 'Apple'>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Add state for success status
  const [successStatus, setSuccessStatus] = useState<'success' | 'failed'>('success');
  const [successMessage, setSuccessMessage] = useState('');
  const [successAction, setSuccessAction] = useState('');

  // Email verification modal state (shown when login fails due to unverified email)
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');

   // Add state for password validation errors
  //  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  
   // Add state for password validation popup
  //  const [showPasswordValidationPopup, setShowPasswordValidationPopup] = useState(false);
 
   // Add state for field validation errors
   const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

   //Apple name modal state 
  //  const [showAppleNameModal, setShowAppleNameModal] = useState(false);
  
  // CAPTCHA states
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [showCaptchaStatus, setShowCaptchaStatus] = useState(false);
  const [captchaStatus, setCaptchaStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');
  const [captchaStatusMessage, setCaptchaStatusMessage] = useState('');
  const [currentCaptchaCode, setCurrentCaptchaCode] = useState('');
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 480);
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // State to show apple login in ios mobiles only
  const [isIOSDevice, setIsIOSDevice] = useState(false); 

  /* Check the device os ios or not */
  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: "GET_IS_IOS" })
      );

      const handler = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          // console.log("Web received:", event.data);
          if (data.action == "IS_IOS") {
            setIsIOSDevice(data.isIOS);
          }
        } catch (e) {
          console.error("Invalid message from RN:", e);
        }
      };

      // 🔥 React Native WebView sends messages through BOTH
      window.addEventListener("message", handler);
      // @ts-ignore - RN dispatches custom event
      document.addEventListener("message", handler);

      return () => {
        window.removeEventListener("message", handler);
        // @ts-ignore
        document.removeEventListener("message", handler);
      };
    } else {
      const ua = navigator.userAgent.toLowerCase();
      const platform = navigator.platform;

      if (platform.includes("mac") || ua.includes("mac")) {
        setIsIOSDevice(true);
        return;
      };
    };

  }, [])
  

  // Redirect if already authenticated
  useEffect(() => {
    // Check if user is already authenticated
    if (isAuthenticated) {
      navigate('/devices');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Clear auth error when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  // iOS browsers (iPad/iPhone Safari, Chrome, Firefox) and the iOS RN WebView
  // can't use the popup OAuth flow, so each hook redirects through the
  // provider in the same window and stashes the result in sessionStorage.
  // Drain those on mount.
  useEffect(() => {
    const appleHandled = processAppleRedirectResult({
      onSuccess: (message) => {
        setOauthInProgress(null);
        const display = getOAuthLoginSuccessDisplay('Apple', message);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setSuccessStatus('success');
        setShowSuccess(true);
        localStorage.setItem('apple-login-used', 'true');
        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      onError: (error) => {
        setOauthInProgress(null);
        const display = getOAuthLoginErrorDisplay('Apple', error);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setShowSuccess(true);
        setSuccessStatus('failed');
      },
    });
    if (appleHandled) setOauthInProgress('Apple');

    const microsoftHandled = processMicrosoftRedirectResult({
      onSuccess: (message) => {
        setOauthInProgress(null);
        const display = getOAuthLoginSuccessDisplay('Microsoft', message);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setSuccessStatus('success');
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      onError: (error) => {
        setOauthInProgress(null);
        const display = getOAuthLoginErrorDisplay('Microsoft', error);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setShowSuccess(true);
        setSuccessStatus('failed');
      },
    });
    if (microsoftHandled) setOauthInProgress('Microsoft');

    const facebookHandled = processFacebookRedirectResult({
      onSuccess: (message) => {
        setOauthInProgress(null);
        const display = getOAuthLoginSuccessDisplay('Facebook', message);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setSuccessStatus('success');
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      onError: (error) => {
        setOauthInProgress(null);
        const display = getOAuthLoginErrorDisplay('Facebook', error);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setShowSuccess(true);
        setSuccessStatus('failed');
      },
    });
    if (facebookHandled) setOauthInProgress('Facebook');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to clear form data
  const clearFormData = () => {
    setFormData({
      username: '',
      password: ''
    });
    setFieldErrors({});
    setShowPassword(false);
  };

  // Clear form data when component mounts (user comes back) or unmounts (user navigates away)
  useEffect(() => {
    // Clear form data on mount
    clearFormData();

    // Clear form data on unmount
    return () => {
      clearFormData();
    };
  }, []);

  // Check if forgotPassword query parameter is present
  useEffect(() => {
    const forgotPasswordParam = searchParams.get('forgotPassword');
    if (forgotPasswordParam === 'true') {
      setShowForgotPassword(true);
      // Clean up URL by removing the query parameter
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  // Checking mobile screens
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

      try {
        await loginSchema.validateAt(name, { [name]: value });
        // Clear field error when field validation passes
        if (fieldErrors[name]) {
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }  
    } catch (validationError) {
      setFieldErrors(prev => ({ ...prev, [name]: (validationError as Error).message }));
    }
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
      try {
      // Additional password validation using existing utility

      // Validate all fields using yup schema
      await loginSchema.validate(formData, { abortEarly: false });

      
      // Clear all errors if validation passes
      setFieldErrors({});
      setIsLoading(true);
      // Simulate API call - show CAPTCHA after registration attempt
      // Show CAPTCHA after form validation
      //   setTimeout(() => {
      //   setShowCaptcha(true);
      //   setIsLoading(false);
      // }, 1000);

      try {
        // Backend no longer accepts the legacy `username` field — send `email`.
        // formData.username is still the local variable holding the typed value.
        const result = await login({
          email: formData.username,
          password: formData.password,
          rememberMe,
        });

        setIsLoading(false);
        if (result.success) {
          // Handle remember me functionality
          setSuccessMessage('Login Successful');
          setSuccessAction('Redirecting to Devices...');
          setSuccessStatus('success');
          setShowSuccess(true);

          // Navigate to devices after a short delay to show success message
          setTimeout(() => {
            navigate('/devices');
          }, 1000);
        }
        else {
          const errorLower = (result.error || '').toLowerCase();
          const apiFailed = errorLower.includes('api connection failed');
          const isUnverifiedEmail =
            errorLower.includes('not verified') ||
            errorLower.includes('verification link') ||
            errorLower.includes('verify to continue');

          if (isUnverifiedEmail) {
            // Dedicated verification modal
            setPendingVerificationEmail(formData.username);
            setShowEmailVerification(true);
          } else if (apiFailed) {
            // Friendly copy when the API itself is unreachable
            setSuccessMessage('Unable to sign in');
            setSuccessAction(
              "We couldn\u2019t connect to the server right now.\nPlease check your internet connection and try again."
            );
            setSuccessStatus('failed');
            setShowSuccess(true);
          } else {
            const display = getLoginErrorDisplay(result.error);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        }
      } catch (error) {
        setIsLoading(false);
        setSuccessMessage('Unable to sign in');
        setSuccessAction(
          "We couldn\u2019t connect to the server right now.\nPlease check your internet connection and try again."
        );
        setSuccessStatus('failed');
        setShowSuccess(true);
      }
    } catch (validationError) {
      if (validationError instanceof yup.ValidationError) {
        const errors: { [key: string]: string } = {};
        validationError.inner.forEach(error => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
        setFieldErrors(prev => ({ ...prev, ...errors }));
      }
    }
  };

  const handleSocialLogin = (loginType: string) => {
    if (loginType.toLowerCase() === 'gmail') {
      // Handle Google OAuth login
      setOauthInProgress('Google');
      const googleLogin = googleLoginWithUserType(
        1, // userType
        (message) => {
            setOauthInProgress(null);
            {
              const display = getOAuthLoginSuccessDisplay(loginType, message);
              setSuccessMessage(display.title);
              setSuccessAction(display.description);
            }
            setSuccessStatus('success');
            setShowSuccess(true);

            // Navigate to devices after a short delay
            setTimeout(() => {
              navigate('/devices');
            }, 1000);
        },
        (error) => {
          setOauthInProgress(null);
          {
            const display = getOAuthLoginErrorDisplay(loginType, error);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
          }
          setShowSuccess(true);
          setSuccessStatus('failed');
        }
      );
      googleLogin();
      return;
    }

    if (loginType.toLowerCase() === 'microsoft') {
      // Handle Microsoft OAuth login
      setOauthInProgress('Microsoft');
      const microsoftLogin = microsoftLoginWithUserType(
        1, // userType
        (message) => {
            setOauthInProgress(null);
            {
              const display = getOAuthLoginSuccessDisplay(loginType, message);
              setSuccessMessage(display.title);
              setSuccessAction(display.description);
            }
            setSuccessStatus('success');
            setShowSuccess(true);

            // Navigate to devices after a short delay
            setTimeout(() => {
              navigate('/devices');
            }, 1000);
        },
        (error) => {
          setOauthInProgress(null);
          {
            const display = getOAuthLoginErrorDisplay(loginType, error);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
          }
          setShowSuccess(true);
          setSuccessStatus('failed');
        }
      );
      microsoftLogin();
      return;
    }

    if (loginType.toLowerCase() === 'facebook') {
      // Handle Facebook OAuth login
      setOauthInProgress('Facebook');
      const facebookLogin = facebookLoginWithUserType(
        1, // userType
        // userType
        (message) => {
            setOauthInProgress(null);
            {
              const display = getOAuthLoginSuccessDisplay(loginType, message);
              setSuccessMessage(display.title);
              setSuccessAction(display.description);
            }
            setSuccessStatus('success');
            setShowSuccess(true);

            // Navigate to devices after a short delay
            setTimeout(() => {
              navigate('/devices');
            }, 1000);
        },
        (error) => {
          setOauthInProgress(null);
          {
            const display = getOAuthLoginErrorDisplay(loginType, error);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
          }
          setShowSuccess(true);
          setSuccessStatus('failed');
        }
      );
      facebookLogin();
      return;
    }


    // Handle other providers with dummy data for now
    // try {
    //   // Simulate social login with dummy data
    //   const socialData = {
    //     loginType: provider.toLowerCase() as 'google' | 'microsoft' | 'uaepass',
    //     accessToken: `dummy_${provider.toLowerCase()}_token_${Date.now()}`,
    //     email: `user@${provider.toLowerCase()}.com`,
    //     name: `${provider} User`,
    //     picture: undefined,
    //     firstName: `${provider} User`,
    //     lastName: `${provider} User`
    //   };

    //   const result = await socialLogin(socialData);
      
    //   if (result.success) {
    //     setSuccessMessage(`${provider} Login Successful`);
    //     setSuccessAction('Redirecting to devices...');
    //     setShowSuccess(true);
    //   } else {
    //     setSuccessMessage(`${provider} Login Failed`);
    //     setSuccessAction(result.error || 'Please try again');
    //     setShowSuccess(true);
    //   }
    // } catch (error) {
    //   setSuccessMessage(`${provider} Login Failed`);
    //   setSuccessAction('An unexpected error occurred');
    //   setShowSuccess(true);
    // }
  };

  // Handle apple login
  // const handleAppleLoginClick = () => {
  //   const appleLoginUsed = localStorage.getItem("apple-login-used");
  //   if (appleLoginUsed == "true") {
  //     handleAppleLogin("", "");
  //   } else {
  //     setShowAppleNameModal(true);
  //   }
  // };

  // Cancel an in-flight OAuth login (overlay Cancel button).
  // Calls the matching hook's cleanup so any late OAUTH_SUCCESS/OAUTH_ERROR
  // messages arriving after this are ignored.
  const handleCancelOAuth = () => {
    if (oauthInProgress === 'Google') cancelGoogleOAuth();
    else if (oauthInProgress === 'Microsoft') cancelMicrosoftOAuth();
    else if (oauthInProgress === 'Facebook') cancelFacebookOAuth();
    else if (oauthInProgress === 'Apple') cancelAppleOAuth();
    setOauthInProgress(null);
  };

  const handleAppleLogin = () => {
    // Handle Apple OAuth login
    // setShowAppleNameModal(false);
    setOauthInProgress('Apple');

    const appleLogin = appleLoginWithUserType(
      1, // userType
      // firstName,
      // lastName,
      (message) => {
        setOauthInProgress(null);
        const display = getOAuthLoginSuccessDisplay('Apple', message);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setSuccessStatus('success');
        setShowSuccess(true);
        localStorage.setItem("apple-login-used", "true");

        setTimeout(() => {
          navigate('/devices');
        }, 1000);
      },
      (error) => {
        setOauthInProgress(null);
        const display = getOAuthLoginErrorDisplay('Apple', error);
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
        setShowSuccess(true);
        setSuccessStatus('failed');
      }
    );
    appleLogin();
    return;
  }

  // CAPTCHA handlers
  const handleCaptchaVerify = async (code: string) => {
    // Show verifying status
    setCaptchaStatus('verifying');
    setCaptchaStatusMessage('Verifying your CAPTCHA response...');
    setShowCaptchaStatus(true);
    setShowCaptcha(false);

    if (code === currentCaptchaCode) {
      // Show success status
      setCaptchaStatus('success');
      setCaptchaStatusMessage('CAPTCHA verification successful! Logging you in...');

      // Call login function after CAPTCHA verification
      try {
        // Backend no longer accepts the legacy `username` field — send `email`.
        const result = await login({
          email: formData.username,
          password: formData.password,
          rememberMe
        });
        
        setTimeout(() => {
          setShowCaptchaStatus(false);
          if (result.success) {
            // Handle remember me functionality
            if (rememberMe) {
              // Save credentials to localStorage
              localStorage.setItem('smarteco_saved_credentials', JSON.stringify({
                username: formData.username,
                password: formData.password
              }));
            } else {
              // Remove saved credentials if remember me is unchecked
              localStorage.removeItem('smarteco_saved_credentials');
            }
          
            setSuccessMessage('Login Successful');
            setSuccessAction('Redirecting to Devices');
            setSuccessStatus('success');
            setShowSuccess(true);
            
            // Navigate to devices after a short delay to show success message
            setTimeout(() => {
              navigate('/devices');
            }, 2000);
          } else {
            const display = getLoginErrorDisplay(result.error);
            setSuccessMessage(display.title);
            setSuccessAction(display.description);
            setSuccessStatus('failed');
            setShowSuccess(true);
          }
        }, 2000);
      } catch (error) {
        setTimeout(() => {
          setShowCaptchaStatus(false);
          const display = getLoginErrorDisplay(
            error instanceof Error ? error.message : undefined
          );
          setSuccessMessage(display.title);
          setSuccessAction(display.description);
          setSuccessStatus('failed');
          setShowSuccess(true);
        }, 2000);
      }
    } else {
      // Show failed status
      setCaptchaStatus('failed');
      setCaptchaStatusMessage('Incorrect CAPTCHA code. Please try again.');
      setCaptchaError(true);
    }
  };

  const handleCaptchaClose = () => {
    setShowCaptcha(false);
    setCaptchaError(false);
  };

  const handleCaptchaErrorClose = () => {
    setCaptchaError(false);
  };

  // Forgot Password handlers
  const handleForgotPasswordSubmit = async (email: string) => {
    setForgotPasswordLoading(true);
    try {
      const result = await forgotPassword(email);
      setShowForgotPassword(false);
      if (result.success) {
        setSuccessStatus('success');
        setSuccessMessage('Password Reset Link Sent');
        setSuccessAction('Your password reset link has been sent. Open it from your inbox to set a new password — don’t forget to check spam if it doesn’t appear.');
      } else {
        const display = getForgotPasswordErrorDisplay(result.error);
        setSuccessStatus('failed');
        setSuccessMessage(display.title);
        setSuccessAction(display.description);
      }
      setShowSuccess(true);
    } catch (error) {
      setShowForgotPassword(false);
      const display = getForgotPasswordErrorDisplay();
      setSuccessMessage(display.title);
      setSuccessAction(display.description);
      setSuccessStatus('failed');
      setShowSuccess(true);
    }
    setForgotPasswordLoading(false);
  };

  const handleForgotPasswordClose = () => {
    setShowForgotPassword(false);
    clearFormData();
  };

  // Success modal handlers
  const handleSuccessClose = () => {
    setShowSuccess(false);
  };

  const handleSuccessAction = () => {
    setShowSuccess(false);
            // If login was successful, navigate to devices
    if (isAuthenticated) {
      navigate('/devices'); 
    }
  };

  return (
      <div className="login-container">
      {/* Header */}
      
      {/* Main Content */}
      <div className={`login-main-content ${ window.innerWidth <= 920 && showForgotPassword ? 'small-screen' : ''}`}>
        {/* Left Section - Login Form */}
        <div className="login-left-section">
           {/* Header */}
           <a href="https://smarteco.ai/" target="_blank" rel="noopener noreferrer" className="cursor-pointer hover:opacity-80 transition-opacity h-[70px] smarteco-logo">
              <img 
                src={`${process.env.PUBLIC_URL}/SVGIcons/NewSmartEcoLogo.svg`}
                alt="Smart Eco Logo" 
                className="h-full w-full object-contain"
              />  
          </a>
          
          <div className="login-form zooomLevelForReportsPage">
            {/* Header */}
            <h2 className="login-title">Welcome Back</h2>
            <p className="subtext">Sign In to your account</p>
            <div className="login-form-container">
              {/* Login Form */}
              <form onSubmit={handleSubmit}>
                {/* Email Field */}
                <div className="input-group">
                  <label className="input-label">Email</label>
                  <div className="input-field-with-icon">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/Email1.svg`} alt="Email" className="input-icon" />
                    <input
                      type="email"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key.length === 1 && !/[a-zA-Z0-9@._-]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                        }
                      }}
                      className={`input-field shadow-sm ${fieldErrors.username ? 'input-error-border' : ''}`}
                      placeholder="Enter your email"
                    />
                  </div>
                  {fieldErrors.username && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {fieldErrors.username}
                      </p>
                    )}
                </div>

                {/* Password Field */}
                <div className="input-group">
                  <label className="input-label">Password</label>
                  <div className="input-field-with-icon">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled1.svg`} alt="Lock" className="input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`input-field shadow-sm ${fieldErrors.password ? 'input-error-border' : ''}`}
                      placeholder="Enter Password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#FFFFFF"/>
                        </svg>
                      ) : (
                        <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#FFFFFF"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* <div className="password-validation-popup-container">
                    <PasswordValidationPopup
                      isVisible={showPasswordValidationPopup}
                      errors={passwordErrors}
                      // password={formData.password}
                      onClose={() => setShowPasswordValidationPopup(false)}
                    />
                  </div> */}
                  {fieldErrors.password && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {fieldErrors.password}
                    </p>
                  )}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="options">
                  <label className="remember-me">
                    <input
                      type="checkbox"
                      className="remember-checkbox"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                    />
                    <span className="checkmark"></span>
                    <span>Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      clearFormData();
                      setShowForgotPassword(true);
                    }}
                    className="forgot-password"
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Sign In Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="signin-button"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing In...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="or-divider">
                <span>OR</span>
              </div>

              {/* Social Login Buttons */}
              <div className="social-login">
                <button
                  onClick={() => handleSocialLogin('Gmail')}
                  className="social-btn gmail-btn"
                >
                  <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.75 0H21L12 7.107L3 0H2.25C1.008 0 0 1.008 0 2.25V15.75C0 16.992 1.008 18 2.25 18H3V3.969L12 10.8915L21 3.9675V18H21.75C22.992 18 24 16.992 24 15.75V2.25C24 1.008 22.992 0 21.75 0Z" fill="#F44336" />
                  </svg>
                  {!isSmallScreen && <span>Gmail</span>}
                </button>

                <button
                  onClick={() => handleSocialLogin('Microsoft')}
                  className="social-btn microsoft-btn"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_423_1037)">
                  <path d="M12.75 11.25H24V0.75C24 0.336 23.664 0 23.25 0H12.75V11.25Z" fill="#4CAF50"/>
                  <path d="M11.25 11.25V0H0.75C0.336 0 0 0.336 0 0.75V11.25H11.25Z" fill="#F44336"/>
                  <path d="M11.25 12.75H0V23.25C0 23.664 0.336 24 0.75 24H11.25V12.75Z" fill="#2196F3"/>
                  <path d="M12.75 12.75V24H23.25C23.664 24 24 23.664 24 23.25V12.75H12.75Z" fill="#FFC107"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_423_1037">
                  <rect width="24" height="24" fill="white"/>
                  </clipPath>
                  </defs>
                </svg>
                  {!isSmallScreen && <span>Microsoft</span>}
                </button>

                <button
                  onClick={() => handleSocialLogin('Facebook')}
                  className="social-btn facebook-btn"
                >
                  <svg width="12" height="22" viewBox="0 0 12 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.53598 12.3421H0.993873C0.583347 12.3421 0.457031 12.1842 0.457031 11.8052C0.457031 10.7789 0.457031 9.72103 0.457031 8.69471C0.457031 8.28419 0.614926 8.12629 0.993873 8.12629H3.53598V5.8684C3.5044 4.87366 3.74124 3.86313 4.23072 2.96313C4.75177 2.06313 5.57282 1.39997 6.52019 1.0684C7.16756 0.831553 7.79914 0.736816 8.47808 0.736816H10.9886C11.3518 0.736816 11.5097 0.894711 11.5097 1.25787V4.17892C11.5097 4.54208 11.3518 4.69997 10.9886 4.69997C10.2939 4.69997 9.59914 4.69997 8.9044 4.73155C8.20966 4.76313 7.8465 5.06313 7.8465 5.78945C7.81493 6.56313 7.8465 7.30524 7.8465 8.09471H10.7991C11.2097 8.09471 11.3676 8.25261 11.3676 8.66313V11.7737C11.3676 12.1842 11.2412 12.3421 10.7991 12.3421H7.87808V20.6947C7.87808 21.1368 7.72019 21.2789 7.29387 21.2789H4.08861C3.70966 21.2789 3.55177 21.121 3.55177 20.7421V12.3421H3.53598Z" fill="url(#paint0_linear_585_771)"/>
                  <defs>
                  <linearGradient id="paint0_linear_585_771" x1="1.77577" y1="5.23997" x2="12.7188" y2="16.1829" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#42A5F5"/>
                  <stop offset="1" stopColor="#1565C0"/>
                  </linearGradient>
                  </defs>
                </svg>
                  {!isSmallScreen && <span>Facebook</span>}
                </button>

                {isIOSDevice && (
                  <button
                    onClick={handleAppleLogin}
                    className="social-btn apple-btn"
                  >
                    <svg width="22px" height="24px" viewBox="0 0 22 24" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"></path> </g></svg>
                    {!isSmallScreen && <span>Apple</span>}
                  </button>
                )}

              </div>

              {/* Sign Up Link */}
              <div className="signup-text">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="signup-link">
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
          
          {/* Copy Right message */}
          <div className='copy-right-message'>© 2026 iSpatial Techno Solutions, All Rights Reserved</div>
        </div>

        {/* Right Section - SmartEco Pro Device */}
        {/* <div className="login-right-section">
          <img src={`${process.env.PUBLIC_URL}/SVGIcons/OfficeScreen1.png`} alt="Login Banner" className="w-full h-full object-contain" />  
        </div> */}
      </div>

      {/* Modals */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={handleForgotPasswordClose}
        onSubmit={handleForgotPasswordSubmit}
        isLoading={forgotPasswordLoading}
      />

      <SuccessModal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        title={successMessage}
        subtitle={successAction}
        status={successStatus}
        actionText={successAction === 'Back to Sign In' ? 'Back to Sign In' : 'OK'}
        onAction={successAction === 'Back to Sign In' ? handleSuccessAction : handleSuccessClose}
      />

      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        defaultEmail={pendingVerificationEmail}
        onResendSuccess={message => {
          setSuccessMessage('Verification email sent');
          setSuccessAction(message);
          setSuccessStatus('success');
          setShowSuccess(true);
        }}
      />

      {/* CAPTCHA Modals */}
      <CaptchaModal
        isOpen={showCaptcha}
        onClose={handleCaptchaClose}
        onVerify={handleCaptchaVerify}
        onCaptchaGenerated={setCurrentCaptchaCode}
        showError={captchaError}
        onErrorClose={handleCaptchaErrorClose}
      />

      <CaptchaStatusModal
        isOpen={showCaptchaStatus}
        status={captchaStatus}
        message={captchaStatusMessage}
        onClose={() => setShowCaptchaStatus(false)}
        actionText={captchaStatus === 'failed' ? 'Try Again' : undefined}
        onAction={captchaStatus === 'failed' ? () => {
          setShowCaptchaStatus(false);
          setShowCaptcha(true);
        } : undefined}
      />

      {/* Apple login firstName and lastname */}
      {/* <AppleNameModal
        isOpen={showAppleNameModal}
        onClose={() => setShowAppleNameModal(false)}
        onSubmit={(firstName, lastName) => {handleAppleLogin(firstName, lastName)}}
      /> */}

      {/* OAuth in-flight overlay — lets the user cancel if the popup was closed */}
      <OAuthLoadingOverlay
        isOpen={oauthInProgress !== null}
        provider={oauthInProgress ?? ''}
        onCancel={handleCancelOAuth}
      />

    </div>
  );
};

export default LoginPage; 